import { Observable } from 'rxjs';

import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { ApiUrlInjectionToken } from '@mitsubishi/shared/data-access';

import {
  FilterValuesResponseDto,
  GoodForSelectionResponseDto,
  StudyCountersDto,
  StudyDetailDto,
  StudyListItemDto,
  StudyUnitDetailDto,
  SubcategoryMetadataDto,
  UnitsQueryRequestDto,
  UnitsQueryResponseDto,
  UnitsWithPerformanceAccessoriesResponseDto,
} from '../models/study-next.model';

/**
 * Thin client for the study-scalability endpoints (contracts/study/openapi.yaml).
 * The only place these URLs live — mock↔real is never an app-code change.
 */
@Injectable({
  providedIn: 'root',
})
export class StudyQueryService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${inject(ApiUrlInjectionToken)}/project`;

  getStudies(projectId: string): Observable<StudyListItemDto[]> {
    return this.http.get<StudyListItemDto[]>(`${this.baseUrl}/${projectId}/studies`);
  }

  getStudy(projectId: string, studyId: string): Observable<StudyDetailDto> {
    return this.http.get<StudyDetailDto>(`${this.baseUrl}/${projectId}/study/${studyId}`);
  }

  /**
   * One unit with its containing subcategory, for the unit-level pages that only know `:unitId` from the
   * route (P1 D8). Valid for external units too — they live in a subcategory of their own.
   */
  getStudyUnit(projectId: string, studyId: string, unitId: string): Observable<StudyUnitDetailDto> {
    return this.http.get<StudyUnitDetailDto>(`${this.baseUrl}/${projectId}/study/${studyId}/unit/${unitId}`);
  }

  getStudyCounters(projectId: string, studyId: string): Observable<StudyCountersDto> {
    return this.http.get<StudyCountersDto>(`${this.baseUrl}/${projectId}/study/${studyId}/counters`);
  }

  getStudyMetadata(projectId: string, studyId: string): Observable<SubcategoryMetadataDto> {
    return this.http.get<SubcategoryMetadataDto>(`${this.baseUrl}/${projectId}/study/${studyId}/metadata`);
  }

  getSubcategoryMetadata(
    projectId: string,
    studyId: string,
    subcategoryId: string
  ): Observable<SubcategoryMetadataDto> {
    return this.http.get<SubcategoryMetadataDto>(
      `${this.baseUrl}/${projectId}/study/${studyId}/subcategory/${subcategoryId}/metadata`
    );
  }

  searchSubcategoryUnits(
    projectId: string,
    studyId: string,
    subcategoryId: string,
    body: UnitsQueryRequestDto
  ): Observable<UnitsQueryResponseDto> {
    return this.http.post<UnitsQueryResponseDto>(
      `${this.baseUrl}/${projectId}/study/${studyId}/subcategory/${subcategoryId}/units/search`,
      body
    );
  }

  /** All-models scope: same body and response, no subcategory in the path (§6.6). */
  searchStudyUnits(projectId: string, studyId: string, body: UnitsQueryRequestDto): Observable<UnitsQueryResponseDto> {
    return this.http.post<UnitsQueryResponseDto>(`${this.baseUrl}/${projectId}/study/${studyId}/units/search`, body);
  }

  getStudyGoodForSelection(projectId: string, studyId: string): Observable<GoodForSelectionResponseDto> {
    return this.http.get<GoodForSelectionResponseDto>(
      `${this.baseUrl}/${projectId}/study/${studyId}/good-for-selection`
    );
  }

  /**
   * The scope's performances that carry accessories, for the heading bar's bulk-assignment dialog.
   * Fetched on demand rather than with the scope: `SubcategoryMetadata.hasPerformanceAccessories`
   * already answers whether the icon should be there, so the list is only worth a request once the
   * user opens the dialog.
   */
  getSubcategoryUnitsWithPerformanceAccessories(
    projectId: string,
    studyId: string,
    subcategoryId: string
  ): Observable<UnitsWithPerformanceAccessoriesResponseDto> {
    return this.http.get<UnitsWithPerformanceAccessoriesResponseDto>(
      `${this.baseUrl}/${projectId}/study/${studyId}/subcategory/${subcategoryId}/units-with-performance-accessories`
    );
  }

  /** All-models scope of {@link getSubcategoryUnitsWithPerformanceAccessories}. */
  getStudyUnitsWithPerformanceAccessories(
    projectId: string,
    studyId: string
  ): Observable<UnitsWithPerformanceAccessoriesResponseDto> {
    return this.http.get<UnitsWithPerformanceAccessoriesResponseDto>(
      `${this.baseUrl}/${projectId}/study/${studyId}/units-with-performance-accessories`
    );
  }

  getSubcategoryFilterValues(
    projectId: string,
    studyId: string,
    subcategoryId: string,
    field: string
  ): Observable<FilterValuesResponseDto> {
    return this.http.get<FilterValuesResponseDto>(
      `${this.baseUrl}/${projectId}/study/${studyId}/subcategory/${subcategoryId}/filter-values`,
      { params: new HttpParams().set('field', field) }
    );
  }

  /** All-models scope: same field and response, no subcategory in the path (§6.6). */
  getStudyFilterValues(projectId: string, studyId: string, field: string): Observable<FilterValuesResponseDto> {
    return this.http.get<FilterValuesResponseDto>(`${this.baseUrl}/${projectId}/study/${studyId}/filter-values`, {
      params: new HttpParams().set('field', field),
    });
  }
}
